import { useState } from 'react';
import { showToastError, showToastSuccess } from '../components/ToastNotifications';
import { editPrintRun, reassignPrintRunProduct } from './usePrintRuns';
import { getErrorMessage } from '../utils/requestErrors';

export type EditPrintRunPayload = Parameters<typeof editPrintRun>[0];

type ReassignParams = {
  payload: EditPrintRunPayload;
  isStaged: boolean;
};

export type ReassignResult =
  | { success: true; coaId: number }
  | { success: false };

export default function useReassignPrintRun(): {
  isReassignSaving: boolean;
  reassign: (params: ReassignParams) => Promise<ReassignResult>;
  reassignIssued: (payload: Parameters<typeof reassignPrintRunProduct>[0]) => Promise<{ success: boolean }>;
} {
  const [isReassignSaving, setIsReassignSaving] = useState(false);

  const reassign = async ({ payload, isStaged }: ReassignParams): Promise<ReassignResult> => {
    setIsReassignSaving(true);
    try {
      const response = await editPrintRun(payload);
      const returnedCoaId = isStaged
        ? (response as { stagePackageCoaId?: unknown } | null | undefined)?.stagePackageCoaId
        : (response as { packageCoaId?: unknown } | null | undefined)?.packageCoaId;
      const numericCoaId = Number(returnedCoaId);
      if (!Number.isSafeInteger(numericCoaId) || numericCoaId <= 0) {
        throw new Error('The updated print run did not include its current COA ID.');
      }

      showToastSuccess('Product reassignment saved');
      return { success: true, coaId: numericCoaId };
    } catch (e) {
      console.error('[StartPrintDialog] Failed to save reassignment:', e);
      showToastError(getErrorMessage(
        e,
        'Failed to save reassignment: product may be archived, outside your organization scope, or not permitted for your role.',
      ));
      return { success: false };
    } finally {
      setIsReassignSaving(false);
    }
  };

  const reassignIssued = async (payload: Parameters<typeof reassignPrintRunProduct>[0]) => {
    setIsReassignSaving(true);
    try {
      await reassignPrintRunProduct(payload);
      showToastSuccess('Product reassignment saved');
      return { success: true };
    } catch (error) {
      showToastError(getErrorMessage(error, 'Failed to save reassignment'));
      return { success: false };
    } finally {
      setIsReassignSaving(false);
    }
  };

  return {
    isReassignSaving,
    reassign,
    reassignIssued,
  };
}
